import { createHash } from 'node:crypto';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { FlatVectorStore, LocalEmbedder, type Embedder, type ScoredRecord } from '@fim/futurex-vector';
import { loadIndex, retrieve, SymbolIndex, ImportGraph, type Chunk } from '@fim/futurex-context';

/** Per-repo index location, keyed by a hash of the absolute repo root. */
export function indexPath(root: string): string {
  const hash = createHash('sha1').update(root).digest('hex').slice(0, 16);
  return join(homedir(), '.futurex', 'index', `${hash}.json`);
}

export interface Retriever {
  readonly size: number;
  /** AST symbol table (§3.5) — answers "is this symbol defined?" without regex. */
  readonly symbols: SymbolIndex;
  /** Import graph — file dependencies + broken-link detection. */
  readonly imports: ImportGraph;
  retrieve(query: string, topK: number): Promise<ScoredRecord<Chunk>[]>;
}

/**
 * Recreate the embedder the index was built with. The id encodes the embedding
 * space (algorithm + dimension) — `local-hash-v1-d<DIM>` — so retrieval
 * reconstructs an exact match. DeepSeek-only: the only embedder is the local one.
 */
function embedderFor(id: string): Embedder | null {
  if (id.startsWith('local-hash-')) return new LocalEmbedder();
  return null;
}

/** Load a persisted index into a retriever, or null if absent/unusable/mismatched. */
export function loadRetriever(root: string): Retriever | null {
  const data = loadIndex(indexPath(root));
  if (!data) return null;
  const embedder = embedderFor(data.embedderId);
  if (!embedder) return null;
  // Embedding-space safety: refuse a stale index whose dimension no longer matches
  // the reconstructed embedder (e.g. a bumped LocalEmbedder DIM) rather than
  // mis-scoring against different-length vectors.
  if (embedder.dimensions !== data.dimensions) return null;

  const store = FlatVectorStore.fromRecords(data.records);
  return {
    size: data.records.length,
    symbols: SymbolIndex.fromJSON(data.symbols ?? []),
    imports: ImportGraph.fromJSON(data.imports ?? []),
    retrieve: (query, topK) => retrieve(store, embedder, query, topK),
  };
}
